import React, { useState } from 'react';
import Select from 'react-select';
import { DOCUMENT_TYPES, getFieldsForDocumentType, COUNTRY_OPTIONS, NATIONALITY_OPTIONS } from '../config/documentConfig';
import { addPassengerDocument, updatePassengerDocument, deletePassengerDocument } from '../services/api';

/**
 * PassengerDocumentManager component for managing passenger identification documents
 * @param {Object} props - Component props
 * @param {number} props.passengerId - Passenger ID
 * @param {Array} props.existingDocuments - Array of existing documents
 * @param {Function} props.onDocumentAdded - Callback when document is added
 * @param {Function} props.onDocumentUpdated - Callback when document is updated
 * @returns {JSX.Element} PassengerDocumentManager component
 */
const PassengerDocumentManager = ({
  passengerId,
  existingDocuments = [],
  onDocumentAdded,
  onDocumentUpdated
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Helper function to get country/nationality name from code
  const getCountryName = (code) => {
    const country = COUNTRY_OPTIONS.find(option => option.value === code);
    return country ? country.label : code || 'N/A';
  };

  const getNationalityName = (code) => {
    const nationality = NATIONALITY_OPTIONS.find(option => option.value === code);
    return nationality ? nationality.label : code || 'N/A';
  };

  // Reset form when document type changes
  const handleDocumentTypeChange = (documentType) => {
    setSelectedDocumentType(documentType);
    setFormData({});
    setError('');
  };

  // Handle form field changes
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Start adding a new document
  const handleStartAdd = () => {
    setShowAddForm(true);
    setEditingDocument(null);
    setSelectedDocumentType('');
    setFormData({});
    setError('');
  };

  // Start editing an existing document
  const handleStartEdit = (document) => {
    setEditingDocument(document);
    setShowAddForm(false);
    setSelectedDocumentType(document.documentType);
    setFormData({
      gender: document.gender || '',
      dateOfBirth: document.dateOfBirth || '',
      documentNumber: document.documentNumber || '',
      dateOfIssue: document.dateOfIssue || '',
      dateOfExpiry: document.dateOfExpiry || '',
      nationality: document.nationality || '',
      placeOfIssue: document.placeOfIssue || '',
      countryOfResidence: document.countryOfResidence || ''
    });
    setError('');
  };

  // Cancel form
  const handleCancel = () => {
    setShowAddForm(false);
    setEditingDocument(null);
    setSelectedDocumentType('');
    setFormData({});
    setError('');
  };

  // Validate form
  const validateForm = () => {
    if (!selectedDocumentType) {
      setError('Please select a document type');
      return false;
    }

    const fields = getFieldsForDocumentType(selectedDocumentType);
    for (const field of fields) {
      if (field.required && !formData[field.name]) {
        setError(`${field.label} is required`);
        return false;
      }
    }

    return true;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const documentData = {
        documentType: selectedDocumentType,
        ...formData
      };

      if (editingDocument) {
        // Update existing document
        await updatePassengerDocument(editingDocument.documentId, documentData);
        onDocumentUpdated && onDocumentUpdated();
      } else {
        // Add new document
        await addPassengerDocument(passengerId, documentData);
        onDocumentAdded && onDocumentAdded();
      }

      handleCancel();
    } catch (error) {
      console.error('Error saving document:', error);
      setError(error.message || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete document
  const handleStartDelete = (document) => {
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete || !documentToDelete.documentId) {
      setError('Cannot delete document: Missing document ID');
      setShowDeleteConfirm(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await deletePassengerDocument(documentToDelete.documentId);
      onDocumentUpdated && onDocumentUpdated();
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error.message || 'Failed to delete document');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDocumentToDelete(null);
  };

  // Render form field
  const renderField = (field) => {
    const value = formData[field.name] || '';

    if (field.type === 'select') {
      // For fields with many options (like nationality and country), use react-select
      const isLargeOptionSet = field.options && field.options.length > 10;

      if (isLargeOptionSet) {
        const selectedOption = field.options.find(option => option.value === value) || null;

        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Select
              value={selectedOption}
              onChange={(selectedOption) => handleFieldChange(field.name, selectedOption ? selectedOption.value : '')}
              options={field.options}
              placeholder={`Select ${field.label}`}
              isSearchable={true}
              isClearable={!field.required}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
                  '&:hover': {
                    borderColor: '#3b82f6'
                  }
                })
              }}
            />
          </div>
        );
      }

      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.name} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type={field.type}
          value={value}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required={field.required}
        />
      </div>
    );
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Identification Documents</h3>
        <button
          onClick={handleStartAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Document
        </button>
      </div>

      {/* Existing Documents */}
      {existingDocuments.length > 0 && (
        <div className="space-y-4 mb-6">
          {existingDocuments.map((document, index) => (
            <div key={document.documentId || index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-gray-900 capitalize">
                  {document.documentType || 'Document'}
                </h4>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleStartEdit(document)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Edit
                  </button>
                  {document.documentId && (
                    <button
                      onClick={() => handleStartDelete(document)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Document Number</span>
                  <span className="font-medium">{document.documentNumber || 'N/A'}</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Gender</span>
                  <span className="font-medium">{document.gender || 'N/A'}</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Date of Birth</span>
                  <span className="font-medium">{document.dateOfBirth || 'N/A'}</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Date of Expiry</span>
                  <span className="font-medium">{document.dateOfExpiry || 'N/A'}</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Nationality</span>
                  <span className="font-medium">{getNationalityName(document.nationality)}</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Place of Issue</span>
                  <span className="font-medium">{document.placeOfIssue || 'N/A'}</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-500">Country of Residence</span>
                  <span className="font-medium">{getCountryName(document.countryOfResidence)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingDocument) && (
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <h4 className="text-lg font-medium mb-4">
            {editingDocument ? 'Edit Document' : 'Add New Document'}
          </h4>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Document Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedDocumentType}
              onChange={(e) => handleDocumentTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={!!editingDocument}
            >
              <option value="">Select Document Type</option>
              {DOCUMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dynamic Fields */}
          {selectedDocumentType && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getFieldsForDocumentType(selectedDocumentType).map(renderField)}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !selectedDocumentType}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                editingDocument ? 'Update Document' : 'Add Document'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {existingDocuments.length === 0 && !showAddForm && !editingDocument && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-600 mb-4">No identification documents found for this passenger.</p>
          <button
            onClick={handleStartAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add First Document
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Document</h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this {documentToDelete?.documentType || 'document'}? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerDocumentManager;
